import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAsync, Async } from '../components/Async';
import { MatchCard } from '../components/MatchCard';
import { ScorersList } from '../components/ScorersList';
import { Countdown } from '../components/Countdown';
import { Flag } from '../components/TeamBadge';
import { teamName } from '../lib/flags';
import { formatKickoff } from '../lib/format';
import { H2, Muted } from '../components/ui';

const NextBanner = styled.button`
  width: 100%;
  text-align: left;
  background: ${({ theme }) => theme.colors.surface};
  border: 2px solid ${({ theme }) => theme.colors.accent};
  border-radius: ${({ theme }) => theme.radius};
  padding: 14px;
  margin-bottom: 12px;
  .lbl { font-size: 0.78rem; color: ${({ theme }) => theme.colors.textMuted}; text-transform: uppercase; letter-spacing: 0.5px; }
  .teams { display: flex; align-items: center; gap: 8px; font-weight: 800; font-size: 1.05rem; margin: 6px 0; flex-wrap: wrap; }
  .cd {
    font-size: 1.5rem;
    font-weight: 800;
    color: ${({ theme }) => theme.colors.accent};
    font-variant-numeric: tabular-nums;
  }
  .when { font-size: 0.82rem; color: ${({ theme }) => theme.colors.textMuted}; }
`;

const SeeAll = styled(Link)`
  display: block;
  text-align: center;
  padding: 10px;
  margin-top: 4px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.accent};
`;

const Hero = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.accent}, ${({ theme }) => theme.colors.accentDark});
  color: #fff;
  border-radius: ${({ theme }) => theme.radius};
  padding: 18px;
  margin-bottom: 8px;
`;

const Stats = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 10px;
  font-size: 0.85rem;
  opacity: 0.95;
  strong {
    display: block;
    font-size: 1.4rem;
  }
`;

export function Home() {
  const navigate = useNavigate();
  const meta = useAsync(() => api.tournament(), [], '/tournament');
  const today = useAsync(() => api.today(), [], '/today');
  const scorers = useAsync(() => api.scorers(), [], '/scorers');

  return (
    <>
      <Async state={meta}>
        {(t) => (
          <>
            <Hero>
              <div style={{ fontSize: '1.5rem', fontWeight: 800 }}>{t.name}</div>
              <Muted style={{ color: 'rgba(255,255,255,0.85)' }}>{t.host}</Muted>
              <Stats>
                <div>
                  <strong>{t.played}</strong>jugados
                </div>
                <div>
                  <strong>{t.remaining}</strong>por jugar
                </div>
                <div>
                  <strong>{t.phase === 'group' ? 'Grupos' : 'Eliminación'}</strong>fase
                </div>
              </Stats>
            </Hero>

            {t.nextMatch && (
              <NextBanner onClick={() => navigate(`/match/${t.nextMatch!.id}`)}>
                <div className="lbl">Próximo partido</div>
                <div className="teams">
                  <Flag team={t.nextMatch.team1.resolved ?? t.nextMatch.team1.name} size={22} />
                  {teamName(t.nextMatch.team1.resolved ?? t.nextMatch.team1.name)}
                  <span style={{ opacity: 0.6 }}>vs</span>
                  <Flag team={t.nextMatch.team2.resolved ?? t.nextMatch.team2.name} size={22} />
                  {teamName(t.nextMatch.team2.resolved ?? t.nextMatch.team2.name)}
                </div>
                <div className="cd">
                  <Countdown to={t.nextMatch.kickoffUtc} />
                </div>
                <div className="when">{formatKickoff(t.nextMatch.kickoffUtc)} · hora local</div>
              </NextBanner>
            )}
          </>
        )}
      </Async>

      <Async state={today}>
        {(d) => (
          <>
            {d.live.length > 0 && (
              <>
                <H2>En juego</H2>
                {d.live.map((m) => (
                  <MatchCard key={m.id} m={m} />
                ))}
              </>
            )}

            <H2>Próximos partidos</H2>
            {d.upcoming.length === 0 ? (
              <Muted>No quedan partidos por jugar.</Muted>
            ) : (
              d.upcoming.map((m) => <MatchCard key={m.id} m={m} />)
            )}

            <H2>Últimos resultados</H2>
            {d.recent.map((m) => (
              <MatchCard key={m.id} m={m} />
            ))}
          </>
        )}
      </Async>

      <H2>🥇 Goleadores</H2>
      <Async state={scorers}>
        {(d) => (
          <>
            <ScorersList scorers={d.scorers.slice(0, 8)} />
            {d.scorers.length > 8 && <SeeAll to="/scorers">Ver ranking completo →</SeeAll>}
          </>
        )}
      </Async>

      <SeeAll to="/stats">Ver estadísticas del torneo →</SeeAll>

      <Muted style={{ textAlign: 'center', margin: '18px 4px 4px', fontSize: '0.92rem' }}>
        Versión gratuita: los datos se actualizan ~2 horas después de cada partido.
      </Muted>
    </>
  );
}
